import React from 'react'

const BidConfirmationContext = React.createContext(null)

export const BidConfirmationProvider = ({ children }) => {
  const [modal, setModal] = React.useState(null)

  const openBidConfirmation = React.useCallback((options) => {
    setModal({
      title: options.title || 'Confirm Action',
      message: options.message || '',
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      tone: options.tone || 'danger',
      onConfirm: options.onConfirm
    })
  }, [])

  const close = React.useCallback(() => setModal(null), [])

  const confirm = React.useCallback(() => {
    const onConfirm = modal?.onConfirm
    setModal(null)
    if (onConfirm) onConfirm()
  }, [modal])

  const confirmStyle = modal?.tone === 'danger'
    ? { background: 'var(--risk, #c62828)', color: '#fff', borderColor: 'var(--risk, #c62828)' }
    : { background: 'var(--ok, #2e7d32)', color: '#fff', borderColor: 'var(--ok, #2e7d32)' }

  return (
    <BidConfirmationContext.Provider value={{ openBidConfirmation }}>
      {children}
      {modal && (
        <div
          className="bid-confirm-overlay"
          onClick={close}
          role="presentation"
        >
          <div
            className="bid-confirm-box"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bid-confirm-title"
          >
            <div id="bid-confirm-title" className="bid-confirm-title">{modal.title}</div>
            <div className="bid-confirm-message">{modal.message}</div>
            <div className="bid-confirm-actions">
              <button className="hmi-btn hmi-btn-ghost" onClick={close}>
                {modal.cancelText}
              </button>
              <button className="hmi-btn" style={confirmStyle} onClick={confirm} autoFocus>
                {modal.confirmText}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes bidConfirmFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes bidConfirmPopIn {
              from { opacity: 0; transform: translateY(10px) scale(0.96); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .bid-confirm-overlay {
              position: fixed;
              inset: 0;
              z-index: 5000;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(20, 28, 24, 0.42);
              animation: bidConfirmFadeIn 140ms ease-out;
            }
            .bid-confirm-box {
              width: min(420px, calc(100vw - 32px));
              background: var(--bg-panel, #fff);
              border: 1px solid var(--line-2, rgba(30, 40, 35, 0.14));
              border-radius: 12px;
              box-shadow: 0 18px 50px rgba(20, 28, 24, 0.24);
              padding: 22px 24px 20px;
              animation: bidConfirmPopIn 160ms ease-out;
            }
            .bid-confirm-title {
              color: var(--ink, #1f2923);
              font-size: 17px;
              font-weight: 700;
              margin-bottom: 8px;
            }
            .bid-confirm-message {
              color: var(--ink-2, #526057);
              font-size: 13px;
              line-height: 1.55;
              margin-bottom: 22px;
              white-space: pre-wrap;
            }
            .bid-confirm-actions {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
            }
          `}</style>
        </div>
      )}
    </BidConfirmationContext.Provider>
  )
}

export const useBidConfirmation = () => {
  const context = React.useContext(BidConfirmationContext)
  if (!context) {
    throw new Error('useBidConfirmation must be used within BidConfirmationProvider')
  }
  return context
}
